import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { phaseStyle } from "@/lib/project_phases";

/**
 * OnTheTable · Sección "¿Qué hay sobre la mesa?" del Dashboard.
 *
 * Mockup D #s1 (bloque inferior bajo el Kanban). Agrega y permite gestionar
 * **tareas** + **próximos hitos del calendario** de TODOS los proyectos del
 * usuario, sin tener que entrar a cada uno.
 *
 * Endpoints consumidos:
 *   · `GET /api/inbox/tasks`
 *   · `POST /api/inbox/tasks`              (text, project_id, due_date?, priority?)
 *   · `PATCH /api/inbox/tasks/{tid}`       (done, text, due_date, priority + project_id)
 *   · `DELETE /api/inbox/tasks/{tid}?project_id=`
 *   · `GET /api/inbox/milestones`
 */
export default function OnTheTable({ projects = [] }) {
  const [tab, setTab] = useState("tasks"); // "tasks" | "milestones"
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inbox/tasks");
      setTasks(r.data || []);
    } catch (_e) { /* silencioso, el sidebar global ya muestra error */ }
    setLoading(false);
  };
  const loadMilestones = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inbox/milestones?window_days=60");
      setMilestones(r.data || []);
    } catch (_e) { /* silencioso */ }
    setLoading(false);
  };

  useEffect(() => {
    loadTasks();
    loadMilestones();
  }, []);

  const counts = useMemo(() => ({
    tasks: tasks.filter((t) => !t.done).length,
    milestones: milestones.length,
  }), [tasks, milestones]);

  const toggleDone = async (task) => {
    setTasks((ts) => ts.map((t) => (t.id === task.id ? { ...t, done: !t.done } : t)));
    try {
      await api.patch(`/inbox/tasks/${task.id}`, { project_id: task.project_id, done: !task.done });
    } catch (_e) {
      // rollback
      loadTasks();
    }
  };

  const remove = async (task) => {
    if (!window.confirm(`¿Eliminar "${task.text}"?`)) return;
    setTasks((ts) => ts.filter((t) => t.id !== task.id));
    try {
      await api.delete(`/inbox/tasks/${task.id}?project_id=${task.project_id}`);
    } catch (_e) {
      loadTasks();
    }
  };

  const createTask = async ({ project_id, text, due_date, priority }) => {
    const r = await api.post("/inbox/tasks", { project_id, text, due_date, priority });
    setTasks((ts) => [...ts, r.data]);
    setCreating(false);
  };

  return (
    <section className="mt-10" data-testid="on-the-table">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="gb-eyebrow">Lo prioritario</p>
          <h2 className="mt-1 gb-display text-2xl">¿Qué hay sobre la mesa?</h2>
          <p className="mt-1 text-sm text-gb-muted">
            Tareas y hitos próximos de todos tus proyectos en un solo sitio.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <SegBtn label="Tareas" count={counts.tasks} active={tab === "tasks"}
                  onClick={() => setTab("tasks")} testid="ott-tab-tasks" />
          <SegBtn label="Próximos hitos" count={counts.milestones} active={tab === "milestones"}
                  onClick={() => setTab("milestones")} testid="ott-tab-milestones" />
          {tab === "tasks" && (
            <button
              onClick={() => setCreating(true)}
              data-testid="ott-new-task"
              className="ml-2 inline-flex items-center gap-2 rounded-full bg-gb-red px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition hover-gb-red"
            >
              + Tarea
            </button>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-gb-paper bg-gb-surface" data-testid="ott-list">
        {loading && tasks.length === 0 && milestones.length === 0 && (
          <p className="px-5 py-8 text-center text-sm text-gb-muted">Cargando…</p>
        )}

        {tab === "tasks" && !loading && tasks.length === 0 && (
          <p className="px-5 py-8 text-center text-sm text-gb-muted">
            Sin tareas. Crea la primera con "+ Tarea".
          </p>
        )}
        {tab === "tasks" && tasks.map((t, idx) => (
          <TaskRow key={t.id} task={t} isLast={idx === tasks.length - 1}
                   onToggle={() => toggleDone(t)} onRemove={() => remove(t)} />
        ))}

        {tab === "milestones" && !loading && milestones.length === 0 && (
          <p className="px-5 py-8 text-center text-sm text-gb-muted">
            No hay hitos próximos. Planifica desde la pestaña Calendario de cada proyecto.
          </p>
        )}
        {tab === "milestones" && milestones.map((m, idx) => (
          <MilestoneRow key={`${m.project_id}-${m.id || idx}`} block={m} isLast={idx === milestones.length - 1} />
        ))}
      </div>

      {creating && (
        <NewTaskModal projects={projects} onCancel={() => setCreating(false)} onSubmit={createTask} />
      )}
    </section>
  );
}

// ─── Rows ────────────────────────────────────────────────────────────────

const TaskRow = ({ task, isLast, onToggle, onRemove }) => {
  const sty = phaseStyle(task.project_phase);
  const prio = PRIORITIES[task.priority || "med"] || PRIORITIES.med;
  const overdue = !task.done && task.due_date && new Date(task.due_date) < new Date();
  return (
    <div className={`flex items-start gap-3 px-5 py-3 ${!isLast ? "border-b border-gb-paper" : ""} ${task.done ? "opacity-50" : ""}`}
         data-testid={`task-row-${task.id}`}>
      <button onClick={onToggle} data-testid={`task-toggle-${task.id}`}
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition ${
                task.done ? "border-emerald-600 bg-emerald-600 text-white" : "border-gb-paper hover:border-gb-red"
              }`}>
        {task.done && <span className="text-[11px] leading-none">✓</span>}
      </button>
      <div className="min-w-0 flex-1">
        <p className={`text-sm leading-snug ${task.done ? "line-through" : ""}`}>{task.text}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gb-muted">
          <Link to={`/project/${task.project_id}`}
                data-testid={`task-project-link-${task.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-gb-paper-soft px-2 py-0.5 transition hover:bg-gb-red/10 hover:text-gb-red">
            <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: sty.hex }} />
            {task.project_title}
          </Link>
          {task.due_date && (
            <span className={overdue ? "font-semibold text-gb-red" : ""}>
              ◷ {fmtDate(task.due_date)}{overdue ? " · vencida" : ""}
            </span>
          )}
          <span className={`rounded-full px-1.5 ${prio.bg} ${prio.text}`}>{prio.label}</span>
        </div>
      </div>
      <button onClick={onRemove} data-testid={`task-delete-${task.id}`}
              className="text-gb-muted/60 transition hover:text-gb-red" title="Eliminar">×</button>
    </div>
  );
};

const MilestoneRow = ({ block, isLast }) => {
  const sty = phaseStyle(block.project_phase);
  return (
    <Link to={`/project/${block.project_id}?tab=calendar`}
          data-testid={`milestone-row-${block.project_id}-${block.id || ''}`}
          className={`flex items-start gap-3 px-5 py-3 transition hover:bg-gb-paper-soft/50 ${
            !isLast ? "border-b border-gb-paper" : ""
          }`}>
      <span className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full`} style={{ background: block.color || sty.hex }} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm leading-snug">{block.label || block.phase || "Bloque sin nombre"}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-gb-muted">
          <span className="inline-flex items-center gap-1 rounded-full bg-gb-paper-soft px-2 py-0.5">
            <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: sty.hex }} />
            {block.project_title}
          </span>
          {block.start && (
            <span className={block.is_overdue ? "font-semibold text-gb-red" : ""}>
              ◷ {fmtDate(block.start)}{block.end && block.end !== block.start ? ` → ${fmtDate(block.end)}` : ""}
              {block.is_overdue ? " · vencido" : ""}
            </span>
          )}
        </div>
      </div>
      <span className="self-center text-gb-muted/60">→</span>
    </Link>
  );
};

const SegBtn = ({ label, count, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
          className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition ${
            active ? "bg-gb-black text-white"
                   : "border border-gb-paper bg-gb-surface text-gb-black/65 hover:border-gb-black/30 hover:text-gb-black"
          }`}>
    {label}
    <span className={`tabular-nums text-[10px] ${active ? "text-white/65" : "text-gb-muted"}`}>{count}</span>
  </button>
);

const PRIORITIES = {
  low: { label: "Baja", bg: "bg-stone-200", text: "text-stone-700" },
  med: { label: "Media", bg: "bg-amber-100", text: "text-amber-900" },
  high: { label: "Alta", bg: "bg-red-100", text: "text-red-700" },
};

// ─── Modal: Nueva tarea ──────────────────────────────────────────────────

const NewTaskModal = ({ projects, onCancel, onSubmit }) => {
  const [project_id, setProjectId] = useState(projects[0]?.id || "");
  const [text, setText] = useState("");
  const [due_date, setDue] = useState("");
  const [priority, setPriority] = useState("med");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!project_id || !text.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({ project_id, text: text.trim(), due_date, priority });
    } catch (_e) {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gb-black/70 p-4 backdrop-blur"
         onClick={onCancel} data-testid="ott-new-task-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="w-full max-w-md rounded-2xl bg-white p-7 shadow-2xl">
        <p className="gb-eyebrow">Sobre la mesa</p>
        <h3 className="mt-1 gb-display text-2xl">Nueva tarea</h3>
        <div className="mt-5 space-y-3">
          <Field label="Proyecto">
            <select value={project_id} onChange={(e) => setProjectId(e.target.value)}
                    data-testid="ott-new-project"
                    className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red">
              {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </Field>
          <Field label="Tarea">
            <input value={text} onChange={(e) => setText(e.target.value)} autoFocus required
                   data-testid="ott-new-text"
                   placeholder="Ej. Revisar tratamiento Cap.4"
                   className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Fecha límite (opcional)">
              <input type="date" value={due_date} onChange={(e) => setDue(e.target.value)}
                     data-testid="ott-new-due"
                     className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red" />
            </Field>
            <Field label="Prioridad">
              <select value={priority} onChange={(e) => setPriority(e.target.value)}
                      data-testid="ott-new-priority"
                      className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red">
                <option value="low">Baja</option>
                <option value="med">Media</option>
                <option value="high">Alta</option>
              </select>
            </Field>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel}
                  className="rounded-full border border-gb-paper px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-muted hover:bg-gb-paper-soft">
            Cancelar
          </button>
          <button type="submit" disabled={submitting || !project_id || !text.trim()}
                  data-testid="ott-new-submit"
                  className="rounded-full bg-gb-red px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover-gb-red disabled:opacity-50">
            {submitting ? "Creando…" : "Crear tarea"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gb-muted">{label}</span>
    {children}
  </label>
);
